import React, { useState } from 'react';
import { Image, Linking, Pressable, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import { Card } from './Card';
import { ScoreRing } from './ScoreRing';
import { ArrowRight, Check, CircleAlert } from './icons';
import { URL_ADZUNA } from '../config/offres';
import { t, tBrut } from '../i18n';
import { colors, formes, spacing, typography } from '../theme/tokens';
import type { Offre } from '../types/offres';
import {
  dureeContrat,
  initiales,
  joursDepuis,
  libelleRaison,
  raisonsAffichees,
  salaireAffiche,
  sourceLibelle,
  titreLong,
} from '../utils/offresAffichage';
import { ContenuLtr } from './ContenuLtr';
import { TEXTE_LTR, isolerLtr } from '../i18n/sens';

export interface OffreCarteProps {
  offre: Offre;
  onVoirDetail: () => void;
  maintenant?: Date;
  testID?: string;
}

/**
 * En dessous de cette hauteur de contenu, la carte passe en mode compact : deux raisons au lieu de
 * trois, écarts serrés, anneau plus petit. C'est le cas de l'iPhone SE (667 pt), où la carte
 * complète dépassait d'une soixantaine de points.
 */
const HAUTEUR_COMPACTE = 380;

export function PastilleEntreprise({ offre, taille }: { offre: Offre; taille: number }) {
  const cadre = { width: taille, height: taille, borderRadius: formes.rayon.md };
  if (offre.entreprise.logo) {
    return (
      <Image
        source={{ uri: offre.entreprise.logo }}
        accessibilityLabel={t('offres.detail.logo', {
          entreprise: offre.entreprise.nom ?? '',
        })}
        style={[cadre, styles.logo]}
        resizeMode="contain"
      />
    );
  }
  return (
    <LinearGradient
      colors={[colors.blueSoft, colors.violetVeil]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[cadre, styles.initiales]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Text style={styles.initialesTexte}>{initiales(offre.entreprise.nom)}</Text>
    </LinearGradient>
  );
}

// Étiquette arrondie des caractéristiques de l'offre : contrat en teinte électrique, le reste en gris.
type Ton = 'accent' | 'violet' | 'neutre';
const TONS: Record<Ton, { fond: object; texte: object }> = {
  accent: { fond: { backgroundColor: colors.blueSoft }, texte: { color: colors.electricInk } },
  // Rythme d'alternance, en violet comme la maquette.
  violet: { fond: { backgroundColor: '#F3EAF8' }, texte: { color: '#7A2FD6' } },
  neutre: { fond: { backgroundColor: colors.grey100 }, texte: { color: colors.grey700 } },
};

function Pastille({ label, ton = 'neutre', ltr = false }: { label: string; ton?: Ton; ltr?: boolean }) {
  const texte = (
    <Text numberOfLines={2} style={[styles.pastilleTexte, TONS[ton].texte, ltr && TEXTE_LTR]}>
      {label}
    </Text>
  );
  return (
    <View style={[styles.pastille, TONS[ton].fond]}>
      {/* Le lieu vient de l'annonce, en français : lu de gauche à droite même dans une interface en arabe. */}
      {ltr ? <ContenuLtr>{texte}</ContenuLtr> : texte}
    </View>
  );
}

// Rythme d'alternance lu dans les raisons du serveur (« rythme » ou « rythme_different » portent la valeur de l'offre).
export function rythmeOffre(offre: Offre): string | null {
  const r = offre.raisons.find((x) => (x.code === 'rythme' || x.code === 'rythme_different') && typeof x.valeur === 'string');
  return r ? t(`offres.rythmes.${r.valeur}`) : null;
}

export function lieuAffiche(offre: Offre): string | null {
  const lieu = offre.lieu.libelle;
  if (!lieu) return null;
  return offre.distanceKm === null ? lieu : t('offres.carte.lieuDistance', { lieu, km: offre.distanceKm });
}

/**
 * Carte d'offre de la pile. Elle prend toute la hauteur que la pile lui donne : la place en plus va
 * au début de la description, en autant de lignes entières qu'il en tient. Sur un petit écran, ce
 * qui ne tient pas est coupé dans la carte — l'offre complète reste dans le panneau de détail.
 */
export function OffreCarte({ offre, onVoirDetail, maintenant, testID }: OffreCarteProps) {
  const [hauteurCorps, setHauteurCorps] = useState(0);
  const [corpsMesure, setCorpsMesure] = useState(false);
  const [hauteurCarte, setHauteurCarte] = useState(0);
  const compacte = hauteurCarte > 0 && hauteurCarte < HAUTEUR_COMPACTE;
  const ecart = compacte ? styles.ecartCompact : styles.ecart;
  // Corps réduit à rien : les raisons touchent le bas de la zone et peuvent être coupées. Sans fond,
  // une coupure en pleine ligne ne se voit pas.
  const serre = compacte || (corpsMesure && hauteurCorps < 1);
  const jours = joursDepuis(offre.publieeLe, maintenant);
  const source = sourceLibelle(offre.source, offre.partenaire);
  const lieu = lieuAffiche(offre);
  const duree = dureeContrat(offre);
  const rythme = rythmeOffre(offre);
  const contrat = t(`offres.contrats.${offre.contrat.code}`);
  const salaire = salaireAffiche(offre);
  // Pas d'extrait de l'annonce sur la carte : il alourdissait la carte, et l'offre complète est à un
  // appui, dans le panneau de détail.
  const adzuna = offre.source === 'adzuna';
  const ligneSource =
    jours === null
      ? source
      : jours === 0
        ? t('offres.carte.publieeAujourdhui', { source })
        : t('offres.carte.publiee', { source, jours, count: jours });

  const avecSalaire = salaire !== null && !compacte;

  // Espaces élastiques entre les blocs : la place qui reste est répartie vers le HAUT de la carte au
  // lieu de s'accumuler en bas. Ils rétrécissent à rien quand la carte est basse.
  const respiration = compacte ? null : <View testID="offre-respiration" style={styles.respiration} />;

  return (
    <Card testID={testID} style={styles.carte}>
      <View
        style={styles.contenu}
        testID="offre-carte-contenu"
        onLayout={(e) => setHauteurCarte(e.nativeEvent.layout.height)}
      >
        {/* Tout ce qui est au-dessus du pied rétrécit et se coupe ; le pied, lui, reste toujours visible. */}
        <View style={[styles.haut, ecart]}>
          <View style={styles.entete} testID="offre-carte-entete">
            <PastilleEntreprise offre={offre} taille={compacte ? 44 : 52} />
            <View style={styles.entrepriseBloc}>
              <ContenuLtr>
                <Text style={[styles.entreprise, TEXTE_LTR]} numberOfLines={2}>
                  {offre.entreprise.nom ?? tBrut('offres.carte.entrepriseInconnue')}
                </Text>
              </ContenuLtr>
              {/* Sous le nom de l'entreprise, comme la maquette. */}
              {adzuna ? (
                // Conditions Adzuna : la mention, liée à leur site, d'au moins 116 × 23 px.
                <Pressable
                  accessibilityRole="link"
                  accessibilityLabel={t('offres.sources.adzuna')}
                  onPress={() => void Linking.openURL(URL_ADZUNA).catch(() => undefined)}
                  style={styles.attribution}
                  hitSlop={{ top: 11, bottom: 11 }}
                >
                  <Text style={[styles.source, styles.sourceLien]} numberOfLines={2}>
                    {ligneSource}
                  </Text>
                </Pressable>
              ) : (
                <Text style={styles.source} numberOfLines={2}>
                  {ligneSource}
                </Text>
              )}
            </View>
            <View
              accessible
              accessibilityLabel={t('offres.carte.score', {
                score: offre.score,
              })}
            >
              <ScoreRing score={offre.score} size={compacte ? 52 : 58} variante="marque" />
            </View>
          </View>

          {respiration}

          {/* Pleine largeur, sous le logo et le score : un intitulé courant y tient sans coupure. */}
          <ContenuLtr>
            <Text style={[styles.titre, titreLong(offre.titre) && styles.titreLong, TEXTE_LTR]} numberOfLines={4}>
              {offre.titre}
            </Text>
          </ContenuLtr>

          {respiration}

          <View style={styles.tags}>
            <Pastille label={contrat} ton="accent" />
            {rythme ? <Pastille label={rythme} ton="violet" /> : null}
            {lieu ? <Pastille label={lieu} ltr /> : null}
            {duree ? <Pastille label={duree} /> : null}
          </View>

          {respiration}

          <View style={[styles.raisons, serre && styles.raisonsCompactes]} testID="offre-raisons">
            {compacte ? null : <Text style={styles.rubrique}>{t('offres.carte.pourquoi')}</Text>}
            {raisonsAffichees(offre.raisons, compacte ? 2 : 3).map((r) => (
              <View
                key={r.code}
                style={styles.raison}
                testID={r.ok ? `offre-raison-${r.code}` : `offre-raison-alerte-${r.code}`}
              >
                <View style={styles.raisonIcone}>
                  {r.ok ? (
                    <Check size={16} color={colors.success} strokeWidth={2.6} />
                  ) : (
                    <CircleAlert size={16} color={colors.corail} strokeWidth={2.4} />
                  )}
                </View>
                <Text style={styles.raisonTexte} numberOfLines={2}>
                  {libelleRaison(r)}
                </Text>
              </View>
            ))}
          </View>

          <View
            style={styles.corps}
            testID="offre-carte-corps"
            onLayout={(e) => {
              setHauteurCorps(e.nativeEvent.layout.height);
              setCorpsMesure(true);
            }}
          >
            {avecSalaire ? (
              <Text style={styles.salaire} numberOfLines={1}>
                {t('offres.carte.salaire', { valeur: isolerLtr(salaire) })}
              </Text>
            ) : null}
          </View>
        </View>

        {/* Hors du corps, qui peut rétrécir à rien : le lien reste visible. Comme la maquette, un seul lien
            en bas à gauche ; il ouvre le détail, d'où part le lien vers l'annonce d'origine. */}
        <Pressable accessibilityRole="button" onPress={onVoirDetail} style={styles.complete} hitSlop={4}>
          <Text style={styles.lienTexte}>{t('offres.carte.voirOffreComplete')}</Text>
          <ArrowRight size={14} color={colors.electricDeep} strokeWidth={2.4} />
        </Pressable>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  // Rayon 28 et marge 20, comme la maquette.
  carte: { flex: 1, overflow: 'hidden', borderRadius: 28, padding: 20 },
  contenu: { flex: 1, minHeight: 0 },
  haut: { flex: 1, minHeight: 0, overflow: 'hidden' },
  // Écart de base entre deux blocs ; les espaces élastiques (`respiration`) s'y ajoutent.
  ecart: { gap: spacing.sm },
  respiration: { flexGrow: 2, flexShrink: 1, minHeight: 0, maxHeight: 40 },
  ecartCompact: { gap: spacing.md },
  entete: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  entrepriseBloc: { flex: 1, minWidth: 0, gap: 2 },
  entreprise: {
    fontSize: 13,
    lineHeight: 17,
    fontWeight: '700',
    color: colors.grey500,
  },
  titre: {
    fontSize: 20,
    lineHeight: 25,
    fontWeight: '800',
    letterSpacing: -0.4,
    color: colors.navy,
  },
  titreLong: {
    fontSize: typography.h3.size,
    lineHeight: typography.h3.lineHeight,
  },
  logo: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  initiales: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initialesTexte: {
    fontSize: typography.h2.size,
    fontWeight: '800',
    color: colors.electricDeep,
  },
  tags: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  pastille: {
    minHeight: 28,
    maxWidth: '100%',
    flexShrink: 1,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
    borderRadius: 14,
    justifyContent: 'center',
  },
  pastilleTexte: { fontSize: 12, lineHeight: 16, fontWeight: '700' },
  // Encadré gris des raisons : se lit d'un coup d'œil, à part de la description.
  // Mesures de la maquette : rayon 18, marge 14, points de 14 pt sur 19 pt de ligne, icônes de 16 pt.
  raisons: { gap: spacing.md, padding: 16, borderRadius: 18, backgroundColor: colors.blueSoft },
  // Petit écran : pas d'encadré. Ce qui ne tient pas est coupé, et un fond coupé en pleine ligne se voit.
  raisonsCompactes: { padding: 0, backgroundColor: 'transparent' },
  rubrique: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    textTransform: 'uppercase',
    color: colors.grey500,
  },
  raison: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  raisonIcone: { marginTop: 2 },
  raisonTexte: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: colors.grey700,
  },
  // Prend la place qui reste entre les raisons et le pied ; `minHeight: 0` le laisse rétrécir à rien
  // sur un petit écran au lieu de pousser le pied hors de la carte.
  corps: { flex: 1, minHeight: 0, overflow: 'hidden', gap: spacing.xs },
  salaire: {
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '600',
    color: colors.night,
  },
  source: {
    fontSize: 12,
    lineHeight: 16,
    color: colors.grey500,
  },
  attribution: { minWidth: 116, minHeight: 23, justifyContent: 'center', alignSelf: 'flex-start' },
  sourceLien: { textDecorationLine: 'underline' },
  complete: { flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 44, alignSelf: 'flex-start', marginTop: spacing.xs },
  // Lien bleu sans soulignement, avec une flèche, comme la maquette.
  lienTexte: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.electricDeep,
  },
});
